import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatCurrency, formatNumber } from '../../../../utils/formatters';

// ── Tipi ──────────────────────────────────────────────────────────────────
interface ComponenteRiga {
  id: number;
  ordinePos: number;
  tipologia: string;
  articoloId: number | null;
  articolo?: { id: number; codice: string; descrizioneBase: string; tipologia?: string } | null;
  codicePrecopy: string | null;
  descrizione: string;
  umVendita: string | null;
  quantita: string | null;
  costoUnitario: string | null;
  prezzoVenditaUnitario: string | null;
}

interface ArticoloLite {
  id: number;
  codice: string;
  descrizioneBase: string;
  tipologia: string;
  umVendita?: { simbolo?: string | null } | null;
  prezzoListinoVendita?: string | null;
}

interface FigliaForm {
  key: string;
  articolo: ArticoloLite | null;
  quantita: string;
  prezzoVenditaUnitario: string;
  costoUnitario: string;
  // riservato all'autocomplete per riga
  search: string;
  searchOpen: boolean;
}

interface ErroreQuadratura {
  forfait?: number;
  sommaFigli?: number;
  differenza?: number;
}

interface Props {
  ordineId: number;
  rigaId: number;
  forfait: number;
  componentiIniziali: ComponenteRiga[];
  societaId: number;
  onClose: () => void;
  onSaved: () => void;
}

// Tipologie ammesse come figlio di esplosione A_CORPO (D64).
const TIPOLOGIE_AMMESSE = 'ARTICOLO_FISICO,LAVORAZIONE_PRESTAZIONE,INTERCAMBIABILE';

let _keySeq = 0;
function nextKey(): string {
  _keySeq += 1;
  return `f-${Date.now()}-${_keySeq}`;
}

function toCents(n: number): number {
  return Math.round(n * 100);
}

// ── Componente principale ─────────────────────────────────────────────────
const ModalEsplosioneRiga: React.FC<Props> = ({
  ordineId,
  rigaId,
  forfait,
  componentiIniziali,
  societaId,
  onClose,
  onSaved,
}) => {
  const [figli, setFigli] = useState<FigliaForm[]>(() => {
    if (componentiIniziali && componentiIniziali.length > 0) {
      return componentiIniziali.map((c) => ({
        key: nextKey(),
        articolo: c.articolo
          ? {
              id: c.articolo.id,
              codice: c.articolo.codice,
              descrizioneBase: c.articolo.descrizioneBase,
              tipologia: c.articolo.tipologia ?? c.tipologia,
              umVendita: c.umVendita ? { simbolo: c.umVendita } : null,
            }
          : null,
        quantita: c.quantita ?? '',
        prezzoVenditaUnitario: c.prezzoVenditaUnitario ?? '',
        costoUnitario: c.costoUnitario ?? '',
        search: '',
        searchOpen: false,
      }));
    }
    return [
      {
        key: nextKey(),
        articolo: null,
        quantita: '',
        prezzoVenditaUnitario: '',
        costoUnitario: '',
        search: '',
        searchOpen: false,
      },
    ];
  });

  const [salvando, setSalvando] = useState(false);
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [dettaglioErrore, setDettaglioErrore] = useState<ErroreQuadratura | null>(null);

  // ── Calcoli quadratura live (centesimi) ─────────────────────────────────
  const centForfait = useMemo(() => toCents(forfait || 0), [forfait]);

  const { centSomma, centCosto, righeInvalide } = useMemo(() => {
    let somma = 0;
    let costo = 0;
    let invalide = 0;
    for (const f of figli) {
      const q = Number(f.quantita);
      const p = Number(f.prezzoVenditaUnitario);
      const c = f.costoUnitario === '' ? null : Number(f.costoUnitario);
      const qOk = Number.isFinite(q) && q > 0;
      const pOk = Number.isFinite(p) && p >= 0;
      const cOk = c === null || (Number.isFinite(c) && c >= 0);
      if (!f.articolo || !qOk || !pOk || !cOk) {
        invalide += 1;
        continue;
      }
      somma += toCents(q * p);
      if (c !== null) costo += toCents(q * c);
    }
    return { centSomma: somma, centCosto: costo, righeInvalide: invalide };
  }, [figli]);

  const quadra = centSomma === centForfait;
  const differenza = (centSomma - centForfait) / 100;
  const margine = (centForfait - centCosto) / 100;
  const confermaAbilitata = quadra && righeInvalide === 0 && figli.length > 0 && !salvando;

  // ── Mutator helpers ──────────────────────────────────────────────────────
  const aggiornaFigliaPatch = (key: string, patch: Partial<FigliaForm>) => {
    setFigli((prev) => prev.map((f) => (f.key === key ? { ...f, ...patch } : f)));
  };

  const aggiungiFiglia = () => {
    setFigli((prev) => [
      ...prev,
      {
        key: nextKey(),
        articolo: null,
        quantita: '',
        prezzoVenditaUnitario: '',
        costoUnitario: '',
        search: '',
        searchOpen: false,
      },
    ]);
  };

  const rimuoviFiglia = (key: string) => {
    setFigli((prev) => prev.filter((f) => f.key !== key));
  };

  const selezionaArticolo = (key: string, a: ArticoloLite) => {
    setFigli((prev) =>
      prev.map((f) => {
        if (f.key !== key) return f;
        const prezzoListino = a.prezzoListinoVendita ?? '';
        return {
          ...f,
          articolo: a,
          // se prezzo vuoto, suggerisci il prezzo listino
          prezzoVenditaUnitario: f.prezzoVenditaUnitario || prezzoListino,
          search: '',
          searchOpen: false,
        };
      }),
    );
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const salva = async () => {
    if (!confermaAbilitata) return;
    setSalvando(true);
    setErroreServer(null);
    setDettaglioErrore(null);
    try {
      const body = {
        figli: figli.map((f) => ({
          articoloId: f.articolo!.id,
          quantita: Number(f.quantita),
          prezzoVenditaUnitario: Number(f.prezzoVenditaUnitario),
          costoUnitario: f.costoUnitario === '' ? undefined : Number(f.costoUnitario),
        })),
      };
      // @api PUT /api/ordini/:id/righe/:rigaId/esplosione {"figli":[{"articoloId":"number","quantita":"number","prezzoVenditaUnitario":"number","costoUnitario":"number?"}]} -> {"success":true,"data":{"rigaId":"number","nComponenti":"number","descrizioneEstesa":"string","margine":"number"}}
      await apiFetch<{
        success: boolean;
        data?: { rigaId: number; nComponenti: number; descrizioneEstesa: string; margine: number };
      }>(`/api/ordini/${ordineId}/righe/${rigaId}/esplosione`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      showToast('Riga esplosa correttamente', 'success');
      onSaved();
    } catch (e) {
      // apiFetch lancia errori con .payload contenente il body parsato.
      const err = e as {
        message?: string;
        status?: number;
        payload?: {
          error?:
            | string
            | { code?: string; message?: string; forfait?: number; sommaFigli?: number; differenza?: number };
        };
      };
      const apiErr = err?.payload?.error;
      if (apiErr && typeof apiErr === 'object') {
        setErroreServer(apiErr.message || 'Errore esplosione');
        if (apiErr.code === 'QUADRATURA_NON_QUADRA') {
          setDettaglioErrore({
            forfait: apiErr.forfait,
            sommaFigli: apiErr.sommaFigli,
            differenza: apiErr.differenza,
          });
        }
      } else if (typeof apiErr === 'string') {
        setErroreServer(apiErr);
      } else {
        setErroreServer(err?.message || 'Errore esplosione');
      }
    } finally {
      setSalvando(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="bop-scheda-ordine__esplosione-overlay">
      <div className="bop-scheda-ordine__esplosione-box">
        <div className="bop-scheda-ordine__esplosione-header">
          <h2 className="bop-scheda-ordine__esplosione-title">Esplosione riga A corpo</h2>
          <button
            type="button"
            className="bop-btn-icon bop-scheda-ordine__esplosione-close"
            onClick={onClose}
            aria-label="Chiudi"
            title="Chiudi"
            disabled={salvando}
          >
            ×
          </button>
        </div>

        <div className="bop-scheda-ordine__esplosione-body">
          <div className="bop-scheda-ordine__esplosione-forfait">
            <span className="bop-text-muted">Forfait riga</span>
            <strong>{formatCurrency(forfait)}</strong>
          </div>

          <table className="bop-table bop-scheda-ordine__esplosione-tabella">
            <thead>
              <tr>
                <th>Articolo</th>
                <th className="bop-text-right">Q.tà</th>
                <th>U.M.</th>
                <th className="bop-text-right">Prezzo vend. unit.</th>
                <th className="bop-text-right">Costo unit.</th>
                <th className="bop-text-right">Subtotale</th>
                <th className="bop-scheda-ordine__esplosione-col-rm"> </th>
              </tr>
            </thead>
            <tbody>
              {figli.map((f) => (
                <RigaFigliaEditabile
                  key={f.key}
                  figlia={f}
                  societaId={societaId}
                  onPatch={(patch) => aggiornaFigliaPatch(f.key, patch)}
                  onSeleziona={(a) => selezionaArticolo(f.key, a)}
                  onRimuovi={() => rimuoviFiglia(f.key)}
                  disabilita={salvando}
                />
              ))}
            </tbody>
          </table>

          <div className="bop-scheda-ordine__esplosione-azioni-figli">
            <button
              type="button"
              className="bop-btn-secondary bop-btn-sm"
              onClick={aggiungiFiglia}
              disabled={salvando}
            >
              + Aggiungi figlio
            </button>
          </div>

          <div
            className={
              'bop-scheda-ordine__esplosione-quadratura' +
              (quadra
                ? ' bop-scheda-ordine__esplosione-quadratura--ok'
                : ' bop-scheda-ordine__esplosione-quadratura--ko')
            }
          >
            <div className="bop-scheda-ordine__esplosione-quadratura-row">
              <span>Somma figli (vendita)</span>
              <strong>{formatCurrency(centSomma / 100)}</strong>
            </div>
            <div className="bop-scheda-ordine__esplosione-quadratura-row">
              <span>Forfait</span>
              <strong>{formatCurrency(centForfait / 100)}</strong>
            </div>
            <div className="bop-scheda-ordine__esplosione-quadratura-row bop-scheda-ordine__esplosione-quadratura-row--delta">
              <span>{quadra ? 'Quadratura' : 'Differenza'}</span>
              <strong>
                {quadra ? 'OK' : `${differenza > 0 ? '+' : ''}${formatCurrency(differenza)}`}
              </strong>
            </div>
            <div className="bop-scheda-ordine__esplosione-quadratura-row bop-scheda-ordine__esplosione-quadratura-row--margine">
              <span>Margine interno (forfait − costi)</span>
              <strong>{formatCurrency(margine)}</strong>
            </div>
            {righeInvalide > 0 && (
              <div className="bop-scheda-ordine__esplosione-quadratura-row bop-text-danger">
                <span>Righe incomplete</span>
                <strong>{formatNumber(righeInvalide)}</strong>
              </div>
            )}
          </div>

          {erroreServer && (
            <div className="bop-scheda-ordine__esplosione-errore">
              <strong>Errore:</strong> {erroreServer}
              {dettaglioErrore && (
                <div className="bop-scheda-ordine__esplosione-errore-dettaglio">
                  Forfait {formatCurrency(dettaglioErrore.forfait ?? 0)} · Somma{' '}
                  {formatCurrency(dettaglioErrore.sommaFigli ?? 0)} · Differenza{' '}
                  {formatCurrency(dettaglioErrore.differenza ?? 0)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bop-scheda-ordine__esplosione-footer">
          <button
            type="button"
            className="bop-btn-secondary"
            onClick={onClose}
            disabled={salvando}
          >
            Annulla
          </button>
          <button
            type="button"
            className="bop-btn-primary"
            onClick={salva}
            disabled={!confermaAbilitata}
            title={
              !quadra
                ? 'La somma dei figli deve coincidere con il forfait'
                : righeInvalide > 0
                ? 'Completa o rimuovi le righe incomplete'
                : 'Conferma esplosione'
            }
          >
            {salvando ? 'Salvataggio…' : 'Conferma esplosione'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Riga figlia editabile + autocomplete articolo ─────────────────────────
interface RigaFigliaProps {
  figlia: FigliaForm;
  societaId: number;
  onPatch: (patch: Partial<FigliaForm>) => void;
  onSeleziona: (a: ArticoloLite) => void;
  onRimuovi: () => void;
  disabilita: boolean;
}

const RigaFigliaEditabile: React.FC<RigaFigliaProps> = ({
  figlia,
  societaId,
  onPatch,
  onSeleziona,
  onRimuovi,
  disabilita,
}) => {
  const [results, setResults] = useState<ArticoloLite[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Subtotale di riga (puramente visuale)
  const subtotale = useMemo(() => {
    const q = Number(figlia.quantita);
    const p = Number(figlia.prezzoVenditaUnitario);
    if (!Number.isFinite(q) || !Number.isFinite(p)) return null;
    return q * p;
  }, [figlia.quantita, figlia.prezzoVenditaUnitario]);

  useEffect(() => {
    if (!figlia.searchOpen) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          societaId: String(societaId),
          tipologie: TIPOLOGIE_AMMESSE,
          attivo: 'true',
          limit: '50',
        });
        if (figlia.search.trim()) params.set('q', figlia.search.trim());
        // @api GET /api/anagrafiche/articoli?societaId&tipologie&q&attivo&limit -> {"success":true,"data":[{"id":"number","codice":"string","descrizioneBase":"string","tipologia":"string","umVendita":{"simbolo":"string"},"prezzoListinoVendita":"string|null"}],"total":"number"}
        const res = await apiFetch<{ success: boolean; data: ArticoloLite[]; total?: number }>(
          `/api/anagrafiche/articoli?${params.toString()}`,
        );
        setResults(Array.isArray(res.data) ? res.data : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [figlia.search, figlia.searchOpen, societaId]);

  const umLabel = figlia.articolo?.umVendita?.simbolo ?? '—';

  return (
    <tr className="bop-scheda-ordine__esplosione-tr">
      <td className="bop-scheda-ordine__esplosione-cell-articolo">
        {figlia.articolo ? (
          <div className="bop-scheda-ordine__esplosione-articolo-sel">
            <div className="bop-scheda-ordine__esplosione-articolo-line1">
              <span className="bop-scheda-ordine__esplosione-articolo-codice">
                {figlia.articolo.codice}
              </span>
              <span className="bop-badge bop-badge--neutral">
                {figlia.articolo.tipologia.replace(/_/g, ' ')}
              </span>
            </div>
            <div className="bop-scheda-ordine__esplosione-articolo-desc">
              {figlia.articolo.descrizioneBase}
            </div>
            <button
              type="button"
              className="bop-btn-secondary bop-btn-sm"
              onClick={() => onPatch({ searchOpen: true, search: '' })}
              disabled={disabilita}
            >
              Cambia articolo
            </button>
          </div>
        ) : (
          <input
            type="text"
            className="bop-input"
            placeholder="Cerca articolo per codice o descrizione…"
            value={figlia.search}
            onChange={(e) => onPatch({ search: e.target.value, searchOpen: true })}
            onFocus={() => onPatch({ searchOpen: true })}
            disabled={disabilita}
          />
        )}

        {figlia.searchOpen && (
          <div className="bop-scheda-ordine__esplosione-autocomplete">
            {loading && (
              <div className="bop-scheda-ordine__esplosione-autocomplete-empty">
                Ricerca in corso…
              </div>
            )}
            {!loading && results.length === 0 && (
              <div className="bop-scheda-ordine__esplosione-autocomplete-empty">
                Nessun articolo trovato
              </div>
            )}
            {!loading &&
              results.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="bop-scheda-ordine__esplosione-autocomplete-item"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSeleziona(a);
                  }}
                >
                  <span className="bop-scheda-ordine__esplosione-articolo-codice">{a.codice}</span>
                  <span className="bop-badge bop-badge--neutral">
                    {a.tipologia.replace(/_/g, ' ')}
                  </span>
                  <span className="bop-scheda-ordine__esplosione-articolo-desc">
                    {a.descrizioneBase}
                  </span>
                  {a.umVendita?.simbolo && (
                    <span className="bop-text-muted">u.m. {a.umVendita.simbolo}</span>
                  )}
                  {a.prezzoListinoVendita && (
                    <span className="bop-scheda-ordine__esplosione-autocomplete-price">
                      {formatCurrency(parseFloat(a.prezzoListinoVendita))}
                    </span>
                  )}
                </button>
              ))}
            <div className="bop-scheda-ordine__esplosione-autocomplete-footer">
              <button
                type="button"
                className="bop-btn-secondary bop-btn-sm"
                onClick={() => onPatch({ searchOpen: false })}
              >
                Chiudi
              </button>
            </div>
          </div>
        )}
      </td>
      <td className="bop-text-right">
        <input
          type="number"
          step="0.01"
          min="0"
          className="bop-input bop-scheda-ordine__esplosione-input-num"
          value={figlia.quantita}
          onChange={(e) => onPatch({ quantita: e.target.value })}
          disabled={disabilita}
        />
      </td>
      <td>{umLabel}</td>
      <td className="bop-text-right">
        <input
          type="number"
          step="0.01"
          min="0"
          className="bop-input bop-scheda-ordine__esplosione-input-num"
          value={figlia.prezzoVenditaUnitario}
          onChange={(e) => onPatch({ prezzoVenditaUnitario: e.target.value })}
          disabled={disabilita}
        />
      </td>
      <td className="bop-text-right">
        <input
          type="number"
          step="0.01"
          min="0"
          className="bop-input bop-scheda-ordine__esplosione-input-num"
          value={figlia.costoUnitario}
          onChange={(e) => onPatch({ costoUnitario: e.target.value })}
          disabled={disabilita}
        />
      </td>
      <td className="bop-text-right">
        {subtotale != null ? formatCurrency(subtotale) : '—'}
      </td>
      <td className="bop-scheda-ordine__esplosione-col-rm">
        <button
          type="button"
          className="bop-btn-icon bop-scheda-ordine__esplosione-btn-rm"
          onClick={onRimuovi}
          disabled={disabilita}
          aria-label="Rimuovi figlio"
          title="Rimuovi figlio"
        >
          ×
        </button>
      </td>
    </tr>
  );
};

export default ModalEsplosioneRiga;
